import React, { useEffect, useState } from 'react';
import { StyledTypography } from './styles/styles';

interface TimerDisplayProps {
  impersonationExpires: number;
  prefixLabel: string | { normal: string; urgent?: string };
  expiredLabel?: string;
  urgentWithSecondsLeft?: number;
  showSeconds?: boolean;
  onExpired?: () => void;
}

const TimerDisplay: React.FC<TimerDisplayProps> = ({
  impersonationExpires,
  prefixLabel,
  urgentWithSecondsLeft,
  expiredLabel = 'Expired',
  showSeconds = true,
  onExpired,
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = Date.now();
      const timeRemaining = Math.max(0, impersonationExpires - now);

      if (timeRemaining <= 0) {
        onExpired?.();
      }

      setTimeLeft(timeRemaining);
    };

    // Calculate immediately
    calculateTimeLeft();

    // Update every second
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [impersonationExpires, onExpired]);

  const formatTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (showSeconds) {
      if (hours > 0) {
        return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
      } else {
        return `${seconds}s`;
      }
    } else {
      // Round up minutes if there are any seconds remaining
      const roundedMinutes = seconds % 60 > 0 ? minutes + 1 : minutes;
      if (hours > 0) {
        return `${hours}h ${roundedMinutes % 60} ${roundedMinutes % 60 === 1 ? 'minute' : 'minutes'}`;
      } else if (roundedMinutes > 0) {
        return `${roundedMinutes} ${roundedMinutes === 1 ? 'minute' : 'minutes'}`;
      } else {
        return '0 minutes';
      }
    }
  };

  if (timeLeft <= 0) {
    return (
      <StyledTypography color="error.main" sx={{ fontSize: '0.875rem' }}>
        {expiredLabel}
      </StyledTypography>
    );
  }

  if (urgentWithSecondsLeft && timeLeft <= urgentWithSecondsLeft * 1000) {
    return (
      <StyledTypography
        color="error.main"
        sx={{
          fontSize: '0.875rem',
          animation: 'pulse-timer 1s infinite',
          '@keyframes pulse-timer': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.4 },
            '100%': { opacity: 1 },
          },
        }}
      >
        {typeof prefixLabel === 'object' ? prefixLabel.urgent || prefixLabel.normal : prefixLabel}{' '}
        {formatTime(timeLeft)}
      </StyledTypography>
    );
  }

  return (
    <StyledTypography color="warning.main" sx={{ fontSize: '0.875rem' }}>
      {typeof prefixLabel === 'object' ? prefixLabel.normal : prefixLabel} {formatTime(timeLeft)}
    </StyledTypography>
  );
};

export default TimerDisplay;
